import { useState, useEffect } from "react";

const TAMANHOS_PAGINA = [10, 20, 50, 100] as const;
export type TamanhoPagina = (typeof TAMANHOS_PAGINA)[number];
export { TAMANHOS_PAGINA };

interface UsePaginacaoLocalResult<T> {
  itensPaginados: T[];
  pagina: number;
  setPagina: (p: number) => void;
  tamanhoPagina: TamanhoPagina;
  setTamanhoPagina: (t: TamanhoPagina) => void;
  totalItens: number;
  totalPaginas: number;
  primeiroItem: number;
  ultimoItem: number;
}

/**
 * Paginação client-side para listas já carregadas em memória.
 * Reseta para a página 1 sempre que `itens` mudar (ex.: ao filtrar).
 */
export function usePaginacaoLocal<T>(
  itens: T[],
  tamanhoPaginaInicial: TamanhoPagina = 20,
): UsePaginacaoLocalResult<T> {
  const [pagina, setPaginaRaw] = useState(1);
  const [tamanhoPagina, setTamanhoPaginaRaw] = useState<TamanhoPagina>(tamanhoPaginaInicial);

  // Reseta para página 1 quando a lista muda (filtro aplicado)
  useEffect(() => {
    setPaginaRaw(1);
  }, [itens]);

  const totalItens = itens.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / tamanhoPagina));
  const paginaSegura = Math.min(pagina, totalPaginas);

  const primeiroItem = totalItens === 0 ? 0 : (paginaSegura - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(paginaSegura * tamanhoPagina, totalItens);

  const itensPaginados = itens.slice(
    (paginaSegura - 1) * tamanhoPagina,
    paginaSegura * tamanhoPagina,
  );

  const setPagina = (p: number) => setPaginaRaw(Math.max(1, Math.min(p, totalPaginas)));
  const setTamanhoPagina = (t: TamanhoPagina) => {
    setTamanhoPaginaRaw(t);
    setPaginaRaw(1);
  };

  return {
    itensPaginados,
    pagina: paginaSegura,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
    totalItens,
    totalPaginas,
    primeiroItem,
    ultimoItem,
  };
}
